// Catalog.ID Client — a button that prints
// ============================================================================
// A browser lets a page open a window only inside a click's user activation, a
// few seconds at most, and `printPdf` prints through a window. So a print must
// START in the click that asks for it: this button calls `print` first and
// synchronously, before it touches anything else, and `printPdf` opens its
// window before any of its own async work. Whatever the print needs from a
// server — keys, wallets, the kit itself — is ready before the button is drawn,
// never fetched inside the click (staging test 2026-10-16: "Finalize & print"
// opened the window after the whole finalize sequence, and Firefox blocked it).
//
// A window blocked anyway (pop-ups switched off for the site) says how to fix
// it, and the button stays, because pressing it again is a fresh click.

import { createElement } from '@catalog-shell';
import { PrintWindowBlockedError } from './pdf-loader';

export interface PrintButtonOptions {
    label: string;
    /** Start the print. Called FIRST in the click, synchronously — no awaiting
     *  before it, here or in the function itself, until `printPdf` has run. */
    print: () => Promise<void>;
    /** The print window is up with the document in it. */
    onPrinted?: () => void;
    /** Where a failure is said. */
    errorEl: HTMLElement;
    className?: string;
}

/** What a failed print tells the person, naming the button to press again. */
export function printFailureMessage(err: unknown, label: string): string {
    if (err instanceof PrintWindowBlockedError) {
        return `Your browser blocked the print window. Allow pop-ups for this site, then press "${label}" again.`;
    }
    return err instanceof Error && err.message ? err.message : 'Printing failed';
}

export function createPrintButton(options: PrintButtonOptions): HTMLButtonElement {
    const button = createElement('button', {
        className: options.className ?? 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: options.label,
    }) as HTMLButtonElement;
    button.addEventListener('click', () => {
        if (button.disabled) return;
        // FIRST: the window has to open inside this click. A print that throws
        // before it is a promise (the kit failed to assemble) is a failure too.
        let job: Promise<void>;
        try {
            job = options.print();
        } catch (err) {
            job = Promise.reject(err);
        }
        button.disabled = true;
        options.errorEl.classList.remove('cid-pane-error--visible');
        job.then(
            () => { options.onPrinted?.(); },
            (err: unknown) => {
                options.errorEl.textContent = printFailureMessage(err, options.label);
                options.errorEl.classList.add('cid-pane-error--visible');
            },
        ).finally(() => { button.disabled = false; });
    });
    return button;
}

/** Where credentials go — said next to the button that prints them, not before
 *  there is anything to print (staging test 2026-10-15). */
export const PHYSICAL_PRINTER_NOTE = 'Send the document set to a physical printer — avoid "Save as PDF" '
    + 'destinations, which would leave your credentials as a file on this machine.';

export interface PrintStageOptions {
    /** One sentence above the button: what is ready, and what printing is for. */
    intro: string;
    label: string;
    print: () => Promise<void>;
    /** The print window is up — draw what follows it (the type-back gate). */
    onPrinted: () => void;
}

/**
 * The print step's own stage, after the work that made the document is done:
 * a sentence and the print button. Once the window is up it removes itself and
 * hands over to `onPrinted`; a failed print leaves it in place, saying why.
 */
export function renderPrintStage(container: HTMLElement, options: PrintStageOptions): void {
    const stage = createElement('div', { className: 'cid-wizard-step' });
    stage.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: options.intro }));
    const errorEl = createElement('div', { className: 'cid-pane-error' });
    stage.appendChild(createPrintButton({
        label: options.label,
        print: options.print,
        errorEl,
        onPrinted: () => {
            stage.remove();
            options.onPrinted();
        },
    }));
    stage.appendChild(errorEl);
    stage.appendChild(createElement('p', { className: 'cid-pane-note', textContent: PHYSICAL_PRINTER_NOTE }));
    container.appendChild(stage);
}
